/*
 * Which language the storefront UI speaks. Pure functions (no i18next, no DOM
 * except the guarded localStorage access), so the rules are easy to test.
 *
 * Precedence, highest first:
 *   1. the visitor's explicit choice (the ID / EN switch), kept in localStorage
 *   2. the shop's `locale` setting (GET /public/store), e.g. "en-US" -> "en"
 *   3. Indonesian
 *
 * Only the interface is translated. Shop content (product and category names,
 * descriptions, CMS pages, home block texts, announcement, the shop's own
 * configured wording) is shown exactly as the seller wrote it.
 */

export const LANGUAGES = ['id', 'en'];
export const DEFAULT_LANGUAGE = 'id';
export const LANGUAGE_STORAGE_KEY = 'mc-store-lang';

// Language names are always written in their own language, so a visitor who
// cannot read the current UI language can still find theirs.
export const LANGUAGE_NAMES = { id: 'Bahasa Indonesia', en: 'English' };

// Locale for dates and plain numbers. Money is always rupiah with Indonesian
// separators ("Rp99.000"), see utils/formatIDR.js.
export const LANGUAGE_LOCALES = { id: 'id-ID', en: 'en-GB' };

// Open Graph spells locales with an underscore.
export const LANGUAGE_OG_LOCALES = { id: 'id_ID', en: 'en_GB' };

/** "en-US" / "EN" / "en_GB" -> "en"; anything we have no translation for -> null. */
export function normalizeLanguage(value) {
  const lang = String(value || '')
    .trim()
    .slice(0, 2)
    .toLowerCase();
  return LANGUAGES.includes(lang) ? lang : null;
}

/** The visitor's explicit choice, or null (never chosen, or storage unavailable). */
export function readStoredLanguage() {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return LANGUAGES.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function persistLanguage(lang) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    /* storage unavailable (private mode, blocked): the choice lasts for this visit only */
  }
}

/** visitor's choice > shop locale > Indonesian */
export function resolveLanguage({ choice = null, shopLocale = null } = {}) {
  return normalizeLanguage(choice) || normalizeLanguage(shopLocale) || DEFAULT_LANGUAGE;
}

export const localeFor = (lang) => LANGUAGE_LOCALES[normalizeLanguage(lang) || DEFAULT_LANGUAGE];
